<section class="sav-ch-hero">
  <div class="sav-site-container">
    <app-breadcrumb [items]="crumbs" />

    <div class="sav-ch-hero-row">
      <div class="sav-ch-hero-main">
        <span class="sav-ch-eyebrow">Browse by type</span>
        <h1 class="sav-ch-title">Coupons by Type</h1>
        <p class="sav-ch-subtitle">
          Jump straight to the kind of savings you want — verified working codes, exclusive deals,
          free shipping offers and student discounts.
        </p>
      </div>

      <dl class="sav-ch-stats">
        <div class="sav-ch-stat">
          <dd class="sav-ch-stat-value">{{ totalOffers() }}</dd>
          <dt class="sav-ch-stat-label">Live offers</dt>
        </div>
        <div class="sav-ch-stat">
          <dd class="sav-ch-stat-value">{{ cards().length }}</dd>
          <dt class="sav-ch-stat-label">Coupon types</dt>
        </div>
        <div class="sav-ch-stat">
          <dd class="sav-ch-stat-value">1 hr</dd>
          <dt class="sav-ch-stat-label">
            Update cycle <app-dev-flag note="Hardcoded stat — not from admin" />
          </dt>
        </div>
      </dl>
    </div>
  </div>
</section>

<section class="sav-ch-grid-section" aria-label="Coupon collections">
  <div class="sav-site-container">
    @if (loading()) {
      <div class="sav-ch-grid" aria-hidden="true">
        @for (n of skeletons; track n) {
          <div class="sav-ch-card is-skeleton">
            <span class="sav-ch-card-head">
              <span class="sav-ch-skel sav-ch-skel-icon"></span>
              <span class="sav-ch-skel sav-ch-skel-badge"></span>
            </span>
            <span class="sav-ch-skel sav-ch-skel-title"></span>
            <span class="sav-ch-skel sav-ch-skel-line"></span>
            <span class="sav-ch-skel sav-ch-skel-line short"></span>
            <span class="sav-ch-card-foot">
              <span class="sav-ch-skel sav-ch-skel-foot"></span>
            </span>
          </div>
        }
      </div>
    } @else if (cards().length === 0) {
      <div class="sav-ch-empty">
        @if (failed()) {
          <h2>We couldn't load coupon types</h2>
          <p>Something went wrong fetching the collections. Please refresh to try again.</p>
        } @else {
          <h2>No coupon types are live right now</h2>
          <p>Collections appear here as soon as coupons are tagged in the admin panel.</p>
        }
        <a routerLink="/collection" class="sav-ch-empty-cta">Browse today's deals</a>
      </div>
    } @else {
      <div class="sav-ch-grid">
        @for (card of cards(); track card.slug) {
          <a class="sav-ch-card" [routerLink]="['/coupons', card.slug]">
            <!-- The badge's admin colour drives the whole card accent: the rail, the icon tint and
                 the pill, so a custom badge added in admin styles itself. -->
            <span
              class="sav-ch-card-rail"
              [style.background]="card.color"
              aria-hidden="true"
            ></span>

            <span class="sav-ch-card-head">
              <span
                class="sav-ch-card-icon"
                [style.background]="iconTint(card.color)"
                [style.color]="card.color"
              >
                @if (card.iconUrl) {
                  <img
                    [src]="card.iconUrl | assetUrl"
                    [alt]=""
                    width="24"
                    height="24"
                    loading="lazy"
                  />
                } @else {
                  @switch (card.slug) {
                    @case ('verified') {
                      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path
                          d="M12 2l2.3 1.7 2.8-.3 1 2.7 2.5 1.3-.7 2.8 1.5 2.4-2 2 .1 2.9-2.8.8-1.6 2.4-2.6-1-2.6 1-1.6-2.4-2.8-.8.1-2.9-2-2 1.5-2.4L2.1 7.4l2.5-1.3 1-2.7 2.8.3L12 2z"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.6"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M8.5 12.2l2.3 2.3 4.4-4.6"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    }
                    @case ('exclusive') {
                      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path
                          d="M4 17l-1.6-9.2 5 3.6L12 4l4.6 7.4 5-3.6L20 17H4zm0 2h16v2H4v-2z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                    @case ('free-shipping') {
                      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path
                          d="M3 5h10a1 1 0 011 1v2h3.2a1 1 0 01.83.44l2.8 4.2a1 1 0 01.17.56V17a1 1 0 01-1 1h-1.05a2.5 2.5 0 01-4.9 0H9.95a2.5 2.5 0 01-4.9 0H3a1 1 0 01-1-1V6a1 1 0 011-1zm11 5v2.2a2.5 2.5 0 013.45.8H19v-1.7L17.13 10H14z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                    @case ('student-discount') {
                      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path
                          d="M12 3l11 6-11 6L1 9l11-6zm7 9.9V17c0 1.66-3.13 3-7 3s-7-1.34-7-3v-4.1l7 3.82 7-3.82z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                    @default {
                      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path
                          d="M20.6 11.5l-8.1-8.1A2 2 0 0011.1 3H5a2 2 0 00-2 2v6.1a2 2 0 00.6 1.4l8.1 8.1a2 2 0 002.8 0l6.1-6.1a2 2 0 000-2.9zM7.5 9A1.5 1.5 0 118.99 7.5 1.5 1.5 0 017.5 9z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  }
                }
              </span>
              <span class="sav-ch-badge" [style.background]="card.color">{{ card.label }}</span>
            </span>

            <h2 class="sav-ch-card-title type-card-title">{{ card.title }}</h2>
            <p class="sav-ch-card-desc">{{ card.description }}</p>

            <span class="sav-ch-card-foot">
              <span class="sav-ch-count">
                @if (card.count > 0) {
                  <strong [style.color]="card.color">{{ card.count }}</strong>
                  <span>offer{{ card.count === 1 ? '' : 's' }}</span>
                } @else {
                  <span class="sav-ch-count-empty">Browse deals</span>
                }
              </span>
              <span class="sav-ch-go">
                View all
                <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                  <path
                    d="M5 12h13m-5-6l6 6-6 6"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </span>
          </a>
        }
      </div>
    }
  </div>
</section>
